"use client";

import { SpecOptionPills } from "@/components/listing/FurnitureOptionalDimensionsFields";
import {
  APPLIANCE_INVERTER_TYPES,
  type ApplianceInverterType,
} from "@/lib/listing/applianceInverterListingSpec";
import {
  FRIDGE_CAPACITY_LITERS_MINI_MAX,
  FRIDGE_CAPACITY_LITERS_MINI_MIN,
  FRIDGE_CAPACITY_LITERS_FULL_MAX,
  FRIDGE_CAPACITY_LITERS_FULL_MIN,
  FRIDGE_DOOR_TYPES,
  FRIDGE_SMART_TYPES,
  formatFridgeDoorTypeLabel,
  type FridgeDoorType,
  type FridgeSmartType,
  type FridgeSpecFormState,
} from "@/lib/listing/fridgeListingSpec";
import { cn } from "@/lib/utils";

interface FridgeSpecFieldsProps {
  value: FridgeSpecFormState;
  onChange: (next: FridgeSpecFormState) => void;
  className?: string;
}

function formatInverterLabel(value: ApplianceInverterType): string {
  return value === "inverter" ? "Inverter" : "Non-inverter";
}

function formatSmartLabel(value: FridgeSmartType): string {
  return value === "smart" ? "Smart" : "Non-smart";
}

/**
 * Step 2 panel for canonical `fridge` (Wave 2 kitchen fridge unified).
 * Door type + inverter + smart required; capacity required except mini door type.
 */
export default function FridgeSpecFields({
  value,
  onChange,
  className = "",
}: FridgeSpecFieldsProps) {
  const isMini = value.fridgeDoorType === "mini";
  const minLiters = isMini
    ? FRIDGE_CAPACITY_LITERS_MINI_MIN
    : FRIDGE_CAPACITY_LITERS_FULL_MIN;
  const maxLiters = isMini
    ? FRIDGE_CAPACITY_LITERS_MINI_MAX
    : FRIDGE_CAPACITY_LITERS_FULL_MAX;

  return (
    <div className={cn("space-y-6", className)}>
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          Door type <span className="text-red-500">*</span>
        </p>
        <SpecOptionPills
          options={[...FRIDGE_DOOR_TYPES]}
          selected={value.fridgeDoorType}
          onSelect={(fridgeDoorType: FridgeDoorType) =>
            onChange({ ...value, fridgeDoorType })
          }
          formatLabel={formatFridgeDoorTypeLabel}
        />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          Capacity (liters)
          {!isMini ? <span className="text-red-500"> *</span> : null}
        </p>
        <input
          type="number"
          inputMode="decimal"
          min={minLiters}
          max={maxLiters}
          placeholder={isMini ? "Optional" : "e.g. 350"}
          value={value.capacityLiters === "" ? "" : value.capacityLiters}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              onChange({ ...value, capacityLiters: "" });
              return;
            }
            const parsed = Number.parseFloat(raw);
            onChange({
              ...value,
              capacityLiters: Number.isFinite(parsed) ? parsed : "",
            });
          }}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          Inverter type <span className="text-red-500">*</span>
        </p>
        <SpecOptionPills
          options={[...APPLIANCE_INVERTER_TYPES]}
          selected={value.applianceInverterType}
          onSelect={(applianceInverterType: ApplianceInverterType) =>
            onChange({ ...value, applianceInverterType })
          }
          formatLabel={formatInverterLabel}
        />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          Smart features? <span className="text-red-500">*</span>
        </p>
        <SpecOptionPills
          options={[...FRIDGE_SMART_TYPES]}
          selected={value.fridgeSmartType}
          onSelect={(fridgeSmartType: FridgeSmartType) =>
            onChange({ ...value, fridgeSmartType })
          }
          formatLabel={formatSmartLabel}
        />
      </div>
    </div>
  );
}
